/**
 * Theme Loader Service
 *
 * Handles loading theme and branding configurations from various sources:
 * 1. Runtime config (includes theme/branding data or URLs)
 * 2. Direct fetch from GCS/CDN
 * 3. Fallback to default theme
 *
 * Uses the same three-tier fetching strategy as the runtime config system.
 */

import {
  ThemeConfig,
  BrandingConfig,
  BrandingStoreInfo,
  LogoConfig,
  DEFAULT_THEME,
  DEFAULT_BRANDING,
} from './themeTypes';

/**
 * Safely read a value from the window runtime env.
 */
function getWindowEnvValue(key: string): string | undefined {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const runtimeEnv = window.__ENV__ as Record<string, string | undefined> | undefined;
  return runtimeEnv?.[key];
}

/**
 * Resolve the configured GCS bucket that hosts store-label assets.
 */
function resolveGcsBucket(): string | undefined {
  return getWindowEnvValue('GCS_BUCKET') || import.meta.env.VITE_GCS_BUCKET;
}

/**
 * Load theme configuration for a store label
 *
 * Fetching strategy:
 * 1. Try fetching from /theme-config.json (generated at runtime or from CDN)
 * 2. Try fetching from /<storeLabel>/theme.json (direct GCS path)
 * 3. Fall back to DEFAULT_THEME
 *
 * @param storeLabel - The store label identifier (e.g., "tayrona", "intersum-ecommerce-example")
 * @returns Promise resolving to ThemeConfig
 */
export async function loadThemeConfig(storeLabel: string): Promise<ThemeConfig> {
  console.log(`[Theme Loader] Loading theme for store label: ${storeLabel}`);

  // Try loading from /theme-config.json (runtime-generated or CDN)
  try {
    const response = await fetch('/theme-config.json');
    if (response.ok) {
      const theme = await response.json();
      console.log('[Theme Loader] Loaded theme from /theme-config.json:', theme);
      return validateThemeConfig(theme);
    }
  } catch (error) {
    console.warn('[Theme Loader] Failed to fetch /theme-config.json:', error);
  }

  // Try loading from GCS public URL pattern
  // This assumes themes are uploaded to GCS at: /<bucket>/store-labels/<label>/theme.json
  const gcsThemeUrl = getGCSThemeUrl(storeLabel);
  if (gcsThemeUrl) {
    try {
      const response = await fetch(gcsThemeUrl);
      if (response.ok) {
        const theme = await response.json();
        console.log(`[Theme Loader] Loaded theme from GCS: ${gcsThemeUrl}`, theme);
        return validateThemeConfig(theme);
      }
    } catch (error) {
      console.warn(`[Theme Loader] Failed to fetch theme from GCS (${gcsThemeUrl}):`, error);
    }
  }

  // Fallback to default theme
  console.warn('[Theme Loader] Using DEFAULT_THEME as fallback');
  return DEFAULT_THEME;
}

/**
 * Load branding configuration for a store label
 *
 * Fetching strategy (same as theme):
 * 1. Try fetching from /branding-config.json (runtime or CDN)
 * 2. Try fetching from /<storeLabel>/branding.json (direct GCS)
 * 3. Fall back to DEFAULT_BRANDING
 *
 * @param storeLabel - The store label identifier
 * @returns Promise resolving to BrandingConfig
 */
export async function loadBrandingConfig(storeLabel: string): Promise<BrandingConfig> {
  console.log(`[Theme Loader] Loading branding for store label: ${storeLabel}`);

  // Try loading from /branding-config.json (runtime-generated or CDN)
  try {
    const response = await fetch('/branding-config.json');
    if (response.ok) {
      const branding = await response.json();
      console.log('[Theme Loader] Loaded branding from /branding-config.json:', branding);
      return validateBrandingConfig(branding);
    }
  } catch (error) {
    console.warn('[Theme Loader] Failed to fetch /branding-config.json:', error);
  }

  // Try loading from GCS public URL pattern
  const gcsBrandingUrl = getGCSBrandingUrl(storeLabel);
  if (gcsBrandingUrl) {
    try {
      const response = await fetch(gcsBrandingUrl);
      if (response.ok) {
        const branding = await response.json();
        console.log(`[Theme Loader] Loaded branding from GCS: ${gcsBrandingUrl}`, branding);
        return validateBrandingConfig(branding);
      }
    } catch (error) {
      console.warn(`[Theme Loader] Failed to fetch branding from GCS (${gcsBrandingUrl}):`, error);
    }
  }

  // Fallback to default branding
  console.warn('[Theme Loader] Using DEFAULT_BRANDING as fallback');
  return DEFAULT_BRANDING;
}

/**
 * Get GCS public URL for theme configuration
 * Constructs URL based on environment variables and store label
 *
 * Expected URL pattern:
 * https://storage.googleapis.com/<bucket>/store-labels/<label>/theme.json
 *
 * @param storeLabel - Store label identifier
 * @returns GCS URL or null if bucket not configured
 */
function getGCSThemeUrl(storeLabel: string): string | null {
  // Check for GCS bucket in environment
  const bucket = resolveGcsBucket();

  if (!bucket) {
    console.warn('[Theme Loader] GCS_BUCKET not configured, skipping GCS fetch');
    return null;
  }

  return `https://storage.googleapis.com/${bucket}/store-labels/${storeLabel}/theme.json`;
}

/**
 * Get GCS public URL for branding configuration
 *
 * @param storeLabel - Store label identifier
 * @returns GCS URL or null if bucket not configured
 */
function getGCSBrandingUrl(storeLabel: string): string | null {
  const bucket = resolveGcsBucket();

  if (!bucket) {
    return null;
  }

  return `https://storage.googleapis.com/${bucket}/store-labels/${storeLabel}/branding.json`;
}

/**
 * Validate and sanitize theme configuration
 * Ensures all required fields exist, falls back to defaults for missing values
 *
 * @param theme - Raw theme object from JSON
 * @returns Validated ThemeConfig
 */
function validateThemeConfig(theme: Partial<ThemeConfig>): ThemeConfig {
  // Merge with default theme to ensure all fields exist
  return {
    name: theme.name || DEFAULT_THEME.name,
    version: theme.version || DEFAULT_THEME.version,
    colors: { ...DEFAULT_THEME.colors, ...theme.colors },
    typography: { ...DEFAULT_THEME.typography, ...theme.typography },
    spacing: { ...DEFAULT_THEME.spacing, ...theme.spacing },
    layout: { ...DEFAULT_THEME.layout, ...theme.layout },
    borders: { ...DEFAULT_THEME.borders, ...theme.borders },
    shadows: { ...DEFAULT_THEME.shadows, ...theme.shadows },
    transitions: { ...DEFAULT_THEME.transitions, ...theme.transitions },
    zIndex: { ...DEFAULT_THEME.zIndex, ...theme.zIndex },
    daisyui: theme.daisyui, // Optional, can be undefined
    darkMode: theme.darkMode, // Optional dark-mode color overrides (stores opt in)
  };
}

/**
 * Validate and sanitize branding configuration
 *
 * @param branding - Raw branding object from JSON
 * @returns Validated BrandingConfig
 */
function validateBrandingConfig(branding: Partial<BrandingConfig>): BrandingConfig {
  const fallbackStore: BrandingStoreInfo = DEFAULT_BRANDING.store || {
    name: DEFAULT_BRANDING.name || 'Store',
    tagline: DEFAULT_BRANDING.tagline || '',
    description: DEFAULT_BRANDING.description || '',
  };

  const store: BrandingStoreInfo = {
    name: branding.store?.name || branding.name || fallbackStore.name,
    tagline: branding.store?.tagline || branding.tagline || fallbackStore.tagline,
    description: branding.store?.description || branding.description || fallbackStore.description,
  };

  const defaultLogo: LogoConfig = DEFAULT_BRANDING.logo || { type: 'text', text: store.name };
  const resolvedLogo = branding.logo ? { ...defaultLogo, ...branding.logo } as LogoConfig : defaultLogo;

  return {
    store: {
      ...fallbackStore,
      ...branding.store,
      ...store,
    },
    name: store.name,
    tagline: store.tagline,
    description: store.description,
    logo: resolvedLogo,
    header: branding.header ?? DEFAULT_BRANDING.header,
    hero: { ...DEFAULT_BRANDING.hero, ...branding.hero },
    about: branding.about ?? DEFAULT_BRANDING.about,
    values: branding.values ?? DEFAULT_BRANDING.values,
    trustSignals: branding.trustSignals ?? DEFAULT_BRANDING.trustSignals,
    enterprise: branding.enterprise ?? DEFAULT_BRANDING.enterprise,
    productListing: branding.productListing ?? DEFAULT_BRANDING.productListing,
    productCard: branding.productCard ?? DEFAULT_BRANDING.productCard,
    productDetail: branding.productDetail ?? DEFAULT_BRANDING.productDetail,
    cart: branding.cart ?? DEFAULT_BRANDING.cart,
    checkout: branding.checkout ?? DEFAULT_BRANDING.checkout,
    footer: branding.footer ?? DEFAULT_BRANDING.footer,
    meta: branding.meta ?? DEFAULT_BRANDING.meta,
    social: branding.social ?? DEFAULT_BRANDING.social,
    contact: branding.contact ?? DEFAULT_BRANDING.contact,
    sectionTitles: branding.sectionTitles ?? DEFAULT_BRANDING.sectionTitles,
  };
}

/**
 * Parse font families out of a stringified font-family CSS declaration.
 * Example: `'Libre Bodoni', Georgia, serif` → ['Libre Bodoni']
 * Only returns the first quoted family (the brand/display font) since
 * the rest are fallback system fonts Google Fonts doesn't host.
 */
function parsePrimaryFontFamily(fontFamilyCss: string | undefined): string | null {
  if (!fontFamilyCss) return null;
  const match = fontFamilyCss.match(/['"]([^'"]+)['"]/);
  return match ? match[1] : null;
}

/**
 * Inject a <link> to Google Fonts for the fonts declared in the current theme.
 * Runs once per unique set of families; replaces any prior injection so
 * switching themes at runtime swaps the link cleanly.
 * Idempotent — if the generated URL hasn't changed, the existing <link> stays.
 */
function injectGoogleFontsLink(theme: ThemeConfig): void {
  if (typeof document === "undefined") return;
  const typography = theme.typography as unknown as Record<string, string | undefined>;
  const candidates = [
    typography.fontFamilyHeading,
    typography.fontFamilyBody,
    typography.fontFamilyPrimary,
    typography.fontFamilyDisplay,
    typography.fontFamilyBrand,
    typography.fontFamilyButton,
  ];
  const families = new Set<string>();
  for (const candidate of candidates) {
    const family = parsePrimaryFontFamily(candidate);
    if (family) families.add(family);
  }
  if (families.size === 0) return;

  // Build Google Fonts CSS2 URL. Load the weights we actually use across
  // headings (400-700), body (300-700), and uppercase buttons (500).
  const familyParams = Array.from(families)
    .sort()
    .map(
      (f) =>
        `family=${encodeURIComponent(f).replace(/%20/g, "+")}:wght@300;400;500;600;700`,
    )
    .join("&");
  const url = `https://fonts.googleapis.com/css2?${familyParams}&display=swap`;

  const linkId = "theme-google-fonts";
  let link = document.getElementById(linkId) as HTMLLinkElement | null;
  if (link && link.href === url) return;
  if (!link) {
    link = document.createElement("link");
    link.id = linkId;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  link.href = url;
  console.log("[Theme Loader] Loaded Google Fonts:", Array.from(families).join(", "));
}

/**
 * Inject theme CSS variables into document root
 * This makes theme tokens available to all components via CSS variables
 *
 * @param theme - Theme configuration to inject
 * @param mode - Theme mode ("light" or "dark"). When "dark" and the theme has
 *   a darkMode block, color variables (and optionally shadow overrides) come
 *   from that block instead of the base theme.
 */
export function injectThemeVariables(theme: ThemeConfig, mode: "light" | "dark" = "light"): void {
  console.log("[Theme Loader] Injecting CSS variables:", theme.name, "mode:", mode);

  const root = document.documentElement;
  root.setAttribute("data-theme-mode", mode);

  // Ensure Google Fonts for this theme's families are loaded. Cheap when
  // already present; rewrites the <link> href if fonts changed.
  injectGoogleFontsLink(theme);

  // Choose color source: dark override when available, otherwise base
  const colors = mode === "dark" && theme.darkMode ? theme.darkMode.colors : theme.colors;

  // Inject color variables
  root.style.setProperty("--color-primary", colors.primary);
  root.style.setProperty("--color-primary-hover", colors.primaryHover);
  root.style.setProperty("--color-primary-light", colors.primaryLight);
  root.style.setProperty("--color-primary-rgb", colors.primaryRgb);

  root.style.setProperty("--color-secondary", colors.secondary);
  root.style.setProperty("--color-secondary-hover", colors.secondaryHover);
  root.style.setProperty("--color-secondary-light", colors.secondaryLight);
  root.style.setProperty("--color-secondary-rgb", colors.secondaryRgb);

  root.style.setProperty("--color-accent", colors.accent);
  root.style.setProperty("--color-accent-hover", colors.accentHover);
  root.style.setProperty("--color-accent-light", colors.accentLight);
  root.style.setProperty("--color-accent-rgb", colors.accentRgb);

  root.style.setProperty("--color-bg", colors.bg);
  root.style.setProperty("--color-bg-secondary", colors.bgSecondary);
  root.style.setProperty("--color-bg-soft", colors.bgSoft);
  root.style.setProperty("--color-bg-rgb", colors.bgRgb);

  root.style.setProperty("--color-text-primary", colors.textPrimary);
  root.style.setProperty("--color-text-secondary", colors.textSecondary);
  root.style.setProperty("--color-text-light", colors.textLight);
  root.style.setProperty("--color-text-on-primary", colors.textOnPrimary);
  root.style.setProperty("--color-text-on-secondary", colors.textOnSecondary);

  root.style.setProperty("--color-border", colors.border);
  root.style.setProperty("--color-border-light", colors.borderLight);
  root.style.setProperty("--color-border-dark", colors.borderDark);

  root.style.setProperty("--color-success", colors.success);
  root.style.setProperty("--color-warning", colors.warning);
  root.style.setProperty("--color-error", colors.error);
  root.style.setProperty("--color-info", colors.info);

  // Inject typography variables. fontFamilyPrimary is the legacy alias;
  // newer themes use fontFamilyBody. We inject both fontFamilyBody under the
  // --font-family-primary CSS var so existing components that read
  // --font-family-primary keep working.
  const typographyExtra = theme.typography as unknown as Record<string, string | undefined>;
  const bodyFamily =
    typographyExtra.fontFamilyPrimary ||
    typographyExtra.fontFamilyBody ||
    "'Inter', sans-serif";
  root.style.setProperty("--font-family-primary", bodyFamily);
  root.style.setProperty("--font-family-body", bodyFamily);
  root.style.setProperty("--font-family-heading", theme.typography.fontFamilyHeading);

  // Optional display/brand/button families
  if (typographyExtra.fontFamilyDisplay) {
    root.style.setProperty("--font-family-display", typographyExtra.fontFamilyDisplay);
  }
  if (typographyExtra.fontFamilyButton) {
    root.style.setProperty("--font-family-button", typographyExtra.fontFamilyButton);
  }
  if ((theme.typography as any).fontFamilyBrand) {
    root.style.setProperty('--font-family-brand', (theme.typography as any).fontFamilyBrand);
  }

  root.style.setProperty('--font-size-xs', theme.typography.fontSizeXs);
  root.style.setProperty('--font-size-sm', theme.typography.fontSizeSm);
  root.style.setProperty('--font-size-base', theme.typography.fontSizeBase);
  root.style.setProperty('--font-size-lg', theme.typography.fontSizeLg);
  root.style.setProperty('--font-size-xl', theme.typography.fontSizeXl);
  root.style.setProperty('--font-size-2xl', theme.typography.fontSize2xl);
  root.style.setProperty('--font-size-3xl', theme.typography.fontSize3xl);
  root.style.setProperty('--font-size-4xl', theme.typography.fontSize4xl);
  root.style.setProperty('--font-size-5xl', theme.typography.fontSize5xl);

  root.style.setProperty('--font-weight-light', String(theme.typography.fontWeightLight));
  root.style.setProperty('--font-weight-normal', String(theme.typography.fontWeightNormal));
  root.style.setProperty('--font-weight-medium', String(theme.typography.fontWeightMedium));
  root.style.setProperty('--font-weight-semibold', String(theme.typography.fontWeightSemibold));
  root.style.setProperty('--font-weight-bold', String(theme.typography.fontWeightBold));

  root.style.setProperty('--line-height-tight', String(theme.typography.lineHeightTight));
  root.style.setProperty('--line-height-normal', String(theme.typography.lineHeightNormal));
  root.style.setProperty('--line-height-relaxed', String(theme.typography.lineHeightRelaxed));

  // New letter spacing properties (with fallbacks)
  if ((theme.typography as any).letterSpacingTight) {
    root.style.setProperty('--letter-spacing-tight', (theme.typography as any).letterSpacingTight);
  }
  if ((theme.typography as any).letterSpacingNormal) {
    root.style.setProperty('--letter-spacing-normal', (theme.typography as any).letterSpacingNormal);
  }
  if ((theme.typography as any).letterSpacingWide) {
    root.style.setProperty('--letter-spacing-wide', (theme.typography as any).letterSpacingWide);
  }

  // Inject spacing variables
  root.style.setProperty('--space-xs', theme.spacing.xs);
  root.style.setProperty('--space-sm', theme.spacing.sm);
  root.style.setProperty('--space-md', theme.spacing.md);
  root.style.setProperty('--space-lg', theme.spacing.lg);
  root.style.setProperty('--space-xl', theme.spacing.xl);
  root.style.setProperty('--space-2xl', theme.spacing.xl2);
  root.style.setProperty('--space-3xl', theme.spacing.xl3);
  root.style.setProperty('--space-4xl', theme.spacing.xl4);

  // Inject layout variables
  root.style.setProperty('--container-max-width', theme.layout.containerMaxWidth);
  root.style.setProperty('--container-padding', theme.layout.containerPadding);
  root.style.setProperty('--header-height', theme.layout.headerHeight);
  root.style.setProperty('--footer-height', theme.layout.footerHeight);

  // Inject border variables
  root.style.setProperty('--border-width', theme.borders.borderWidth);
  root.style.setProperty('--border-width-thick', theme.borders.borderWidthThick);
  root.style.setProperty('--radius-none', theme.borders.radiusNone);
  root.style.setProperty('--radius-sm', theme.borders.radiusSm);
  root.style.setProperty('--radius-md', theme.borders.radiusMd);
  root.style.setProperty('--radius-lg', theme.borders.radiusLg);
  root.style.setProperty('--radius-xl', theme.borders.radiusXl);
  root.style.setProperty('--radius-full', theme.borders.radiusFull);

  // Inject shadow variables (darkMode.shadows overrides on top of base)
  const shadows = {
    ...theme.shadows,
    ...(mode === "dark" && theme.darkMode?.shadows ? theme.darkMode.shadows : {}),
  };
  root.style.setProperty("--shadow-sm", shadows.sm);
  root.style.setProperty("--shadow-md", shadows.md);
  root.style.setProperty("--shadow-lg", shadows.lg);
  root.style.setProperty("--shadow-xl", shadows.xl);

  // Inject transition variables
  root.style.setProperty('--transition-fast', theme.transitions.fast);
  root.style.setProperty('--transition-base', theme.transitions.base);
  root.style.setProperty('--transition-slow', theme.transitions.slow);

  // Inject z-index variables
  root.style.setProperty('--z-dropdown', String(theme.zIndex.dropdown));
  root.style.setProperty('--z-sticky', String(theme.zIndex.sticky));
  root.style.setProperty('--z-fixed', String(theme.zIndex.fixed));
  root.style.setProperty('--z-modal-backdrop', String(theme.zIndex.modalBackdrop));
  root.style.setProperty('--z-modal', String(theme.zIndex.modal));
  root.style.setProperty('--z-popover', String(theme.zIndex.popover));
  root.style.setProperty('--z-tooltip', String(theme.zIndex.tooltip));

  // Inject DaisyUI theme variables if provided
  if (theme.daisyui) {
    injectDaisyUITheme(theme.daisyui);
  }

  console.log('[Theme Loader] CSS variables injected successfully');
}

/**
 * Inject DaisyUI theme variables
 * DaisyUI uses data-theme attribute and CSS variables
 *
 * @param daisyTheme - DaisyUI theme configuration
 */
function injectDaisyUITheme(daisyTheme: ThemeConfig['daisyui']): void {
  if (!daisyTheme) return;

  const root = document.documentElement;

  // Set data-theme attribute for DaisyUI
  root.setAttribute('data-theme', 'custom');

  // Inject DaisyUI semantic colors
  root.style.setProperty('--p', daisyTheme.primary);
  root.style.setProperty('--pc', daisyTheme['primary-content']);
  root.style.setProperty('--s', daisyTheme.secondary);
  root.style.setProperty('--sc', daisyTheme['secondary-content']);
  root.style.setProperty('--a', daisyTheme.accent);
  root.style.setProperty('--ac', daisyTheme['accent-content']);
  root.style.setProperty('--n', daisyTheme.neutral);
  root.style.setProperty('--nc', daisyTheme['neutral-content']);
  root.style.setProperty('--b1', daisyTheme['base-100']);
  root.style.setProperty('--b2', daisyTheme['base-200']);
  root.style.setProperty('--b3', daisyTheme['base-300']);
  root.style.setProperty('--bc', daisyTheme['base-content']);
  root.style.setProperty('--in', daisyTheme.info);
  root.style.setProperty('--su', daisyTheme.success);
  root.style.setProperty('--wa', daisyTheme.warning);
  root.style.setProperty('--er', daisyTheme.error);

  console.log('[Theme Loader] DaisyUI theme variables injected');
}

// Type declaration for window.__ENV__ is now in vite-env.d.ts
